import React, { useContext, useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { SanityImage, buildSrc, buildSrcSet } from 'sanity-image';
import Vimeo from '@u-wave/react-vimeo';
import Helmet from 'react-helmet';
import { FaPause, FaPlay } from 'react-icons/fa';
import { PortableText } from '@portabletext/react';

import { LocaleContext } from '@rsc/contexts/LocaleContext';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

import breakpoints from '../breakpoints';
import Button from '../button';
import { Button as ShadcnButon } from '../shadcn/Button';
import sharedStyles from '../styles';

const sanityBaseUrl = `https://cdn.sanity.io/images/${process.env.GATSBY_SANITY_PROJECT_ID}/${process.env.GATSBY_SANITY_DATASET}/`;

const Presentation = styled.div`
  ${({ presentation }) =>
    presentation === 'full-width' &&
    css`
      width: 100vw;
      position: relative;
      left: 50%;
      right: 50%;
      margin-left: -50vw;
      margin-right: -50vw;
    `}

  margin-bottom: ${sharedStyles.spacing['spacing-lg']};
`;

const HeroInner = styled.div`
  position: relative;
  min-height: 90px;

  @media (min-width: ${breakpoints.md}) {
    height: auto;
    min-height: 150px;
  }
`;

const HeroText = styled.div`
  padding-inline: ${sharedStyles.spacing['spacing-lg']};
  align-items: center;
  z-index: 1;

  @media (min-width: ${breakpoints.md}) {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    text-align: left;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    height: 100%;
    width: 100%;
  }

  @media (min-width: ${breakpoints.xl}) {
    max-width: ${breakpoints.xl};
    ${({ presentation }) =>
      presentation === 'full-width' &&
      css`
        margin-left: calc((100% - ${breakpoints.xl}) / 2);
      `}
    ${({ presentation }) =>
      presentation === 'content-width' &&
      css`
        margin-left: revert;
      `}
  }
`;

const HeroTextColor = styled.div`
  h1 {
    color: ${({ theme }) => theme.colors.hero.mobileHeadlineColor};
  }

  span {
    color: inherit;
  }

  @media (min-width: ${breakpoints.md}) {
    h1 {
      ${({ headlineStyle, theme }) => {
        if (headlineStyle) {
          return `color: ${theme.colors?.hero?.[headlineStyle]?.color};`;
        }

        return `color: #ffffff;`;
      }}
    }
    span {
      ${({ subheadlineStyle, theme }) => {
        if (subheadlineStyle) {
          return `color: ${theme.colors?.hero?.[subheadlineStyle]?.color};`;
        }

        return `color: #ffffff;`;
      }}
    }
  }
`;

const ImageContainer = styled.div`
  img {
    ${({ compactImage }) =>
      compactImage &&
      `
      aspect-ratio: 21/9;

      @media (min-width: ${breakpoints.xl}) {
        aspect-ratio: 42/9;
        height: 50%;
      }
    `};

    ${({ compactImage }) =>
      !compactImage &&
      `
      aspect-ratio: 1/1;

      @media (min-width: ${breakpoints.sm}) {
        aspect-ratio: 3/2;
      }

      @media (min-width: ${breakpoints.md}) {
        aspect-ratio: 2/1;
      }

      @media (min-width: ${breakpoints.lg}) {
        aspect-ratio: 21/9;
      }
  `};
  }
`;

const HeroImage = styled(SanityImage)`
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  min-height: 90px;

  ${({ hotspot }) =>
    hotspot ? `object-position: ${hotspot.x * 100}% ${hotspot.y * 100}%;` : ''}
`;

const StaticHeroImage = styled.img`
  display: block;
  object-fit: cover;
  object-position: ${({ mobileCrop }) => mobileCrop || ''};
  height: 250px;
  min-height: 90px;
  width: 100%;

  @media (min-width: ${breakpoints.sm}) {
    height: auto;
  }

  @media (min-width: ${breakpoints.md}) {
    min-height: revert;
    display: flex;
  }
`;

const Box = styled.div`
  display: flex;
  flex-direction: column-reverse;
`;

const Headline = styled.h1`
  margin-bottom: 0;
  white-space: pre-wrap;

  @media (min-width: ${breakpoints.md}) {
    font-size: clamp(2.25rem, 3vw + 1rem, 4rem);

    ${({ enableTextShadow }) =>
      enableTextShadow &&
      css`
        text-shadow: 1px 1px 43px #000000;
      `};
  }
`;

const StyledPortableHeadline = styled.span`
  margin: 0;
  display: block;
  font-weight: bold;
  line-height: 1.15;
  white-space: pre-wrap;
  text-transform: ${({ theme }) => theme.hero.span.textTransform};

  @media (min-width: ${breakpoints.md}) {
    ${({ enableTextShadow }) =>
      enableTextShadow &&
      css`
        text-shadow: 1px 1px 43px #000000;
      `};
  }

  &.small {
    font-size: ${sharedStyles.fontSize.base};
  }

  &.large {
    font-size: ${sharedStyles.fontSize['1xl']};
  }

  font-size: ${sharedStyles.fontSize.lg};

  @media (min-width: ${breakpoints.lg}) {
    &.small {
      font-size: ${sharedStyles.fontSize.lg};
    }

    &.large {
      font-size: ${sharedStyles.fontSize['2xl']};
    }

    font-size: ${sharedStyles.fontSize['1xl']};
  }
`;

const SubHeadline = styled.span`
  margin: 0;
  font-size: ${sharedStyles.fontSize.lg};
  display: block;
  font-weight: bold;
  line-height: 1.15;
  white-space: pre-wrap;
  text-transform: ${({ theme }) => theme.hero.span.textTransform};

  @media (min-width: ${breakpoints.md}) {
    ${({ enableTextShadow }) =>
      enableTextShadow &&
      css`
        text-shadow: 1px 1px 43px #000000;
      `};
  }

  @media (min-width: ${breakpoints.lg}) {
    font-size: ${sharedStyles.fontSize['1xl']};
  }
`;

const HorizontalRule = styled.hr`
  margin-block: 0.25em;
  height: 0.25rem;
  color: ${({ theme }) => theme.colors.brandPrimary};
  background: ${({ theme }) => theme.colors.brandPrimary};
  border: none;
`;

const CtaContainer = styled.div`
  a {
    margin-top: 1rem;
  }
`;

const GridItem = styled.div`
  grid-area: revert;
  text-align: center;
  margin-top: 1rem;

  @media (min-width: ${breakpoints.md}) {
    margin: 0;
  }

  ${({ textAlign }) => {
    switch (textAlign) {
      case 'topCenter':
        return `
        grid-row: 1;
        grid-column: 2;
      `;
      case 'topRight':
        return `
        grid-row: 1;
        grid-column: 3;
      `;
      case 'middleLeft':
        return `
        grid-row: 2;
        grid-column: 1;
      `;
      case 'middleCenter':
        return `
        grid-row: 2;
        grid-column: 2;
      `;
      case 'middleRight':
        return `
        grid-row: 2;
        grid-column: 3;
      `;
      case 'bottomLeft':
        return `
        grid-row: 3;
        grid-column: 1;
      `;
      case 'bottomCenter':
        return `
        grid-row: 3;
        grid-column: 2;
      `;
      case 'bottomRight':
        return `
        grid-row: 3;
        grid-column: 3;
      `;
      default:
        return `
          grid-row: 1;
          grid-column: 1;
        `;
    }
  }};
`;

const StyledVimeo = styled(Vimeo)`
  display: ${({ isLoading }) => (isLoading ? 'none' : 'block')};
`;

const VideoPreview = styled.img`
  display: ${({ isLoading, isError }) =>
    isLoading || isError ? 'block' : 'none'};
`;

const getHeadline = (
  headline1,
  headline2,
  portableTextHeadline,
  subheadType,
  enableTextShadow
) => (
  <div>
    {headline1 && (
      <Headline enableTextShadow={enableTextShadow}>
        {headline1?.replaceAll('\\n', '\n')}
      </Headline>
    )}
    {headline1 && (headline2 || portableTextHeadline?.length > 0) && (
      <HorizontalRule aria-hidden="true" />
    )}
    {(!subheadType || subheadType === '' || subheadType === 'text') &&
      headline2 && (
        <SubHeadline enableTextShadow={enableTextShadow}>
          {headline2?.replaceAll('\\n', '\n')}
        </SubHeadline>
      )}
    {subheadType === 'portableText' && portableTextHeadline?.length > 0 && (
      <div>
        <PortableText
          value={portableTextHeadline}
          components={{
            block: {
              normal: ({ children }) => (
                <StyledPortableHeadline>{children}</StyledPortableHeadline>
              ),
              small: ({ children }) => (
                <StyledPortableHeadline
                  className="small"
                  enableTextShadow={enableTextShadow}
                >
                  {children}
                </StyledPortableHeadline>
              ),
              large: ({ children }) => (
                <StyledPortableHeadline
                  className="large"
                  enableTextShadow={enableTextShadow}
                >
                  {children}
                </StyledPortableHeadline>
              ),
            },
          }}
        />
      </div>
    )}
  </div>
);

const getCta = (enableCta, cta, getInternalLink, importance) =>
  enableCta &&
  cta?.caption && (
    <CtaContainer>
      {cta?.linkType === 'internal' ? (
        <Button
          to={getInternalLink({ ...cta, reference: cta?.internalLink })}
          importance={importance}
        >
          {cta?.caption}
        </Button>
      ) : (
        <Button href={cta?.href} importance={importance}>
          {cta?.caption}
        </Button>
      )}
    </CtaContainer>
  );

const getHeroImage = (imageSrc, mobileCrop, compactImage) => (
  <ImageContainer compactImage={compactImage}>
    {imageSrc?.asset?._id ? (
      <HeroImage
        id={imageSrc.asset._id}
        baseUrl={sanityBaseUrl}
        alt={imageSrc.alt || ''}
        loading="eager"
        hotspot={imageSrc?.hotspot}
        crop={imageSrc?.crop}
        queryParams={{
          fit: 'crop',
          crop: 'focalpoint',
          'fp-x': imageSrc?.hotspot?.x || '',
          'fp-y': imageSrc?.hotspot?.y || '',
        }}
      />
    ) : (
      <StaticHeroImage
        src={imageSrc}
        alt={imageSrc.alt || ''}
        mobileCrop={mobileCrop}
      />
    )}
  </ImageContainer>
);

function Hero({
  hero = {
    heroType: '',
    compactImage: false,
    externalVideo: '',
    internalVideo: {},
    heroPoster: {},
    headline1: '',
    headline2: '',
    headlineStyle: '',
    subheadType: '',
    portableTextHeadline: [],
    subheadlineStyle: '',
    mediaItem: {},
    alt: 'Hero',
    enableTextShadow: false,
    enableCta: false,
    cta: {},
    themeStyle: 'primary',
    presentation: '',
    mobileCrop: '',
    altText: '',
  },
  getInternalLink,
}) {
  const {
    heroType,
    compactImage,
    externalVideo,
    internalVideo,
    heroPoster,
    textAlignment,
    headline1,
    headline2,
    headlineStyle,
    subheadType,
    portableTextHeadline,
    subheadlineStyle,
    mediaItem,
    enableTextShadow,
    enableCta,
    cta,
    themeStyle,
    presentation,
    mobileCrop,
  } = hero;

  const { tt } = useUiComponentsInternationalContext();

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const localeContext = useContext(LocaleContext);
  const videoRef = useRef();
  const currency = localeContext?.currency || 'USD';
  const poster =
    heroPoster?.asset?.url && `${heroPoster.asset.url}?auto=format`;

  const getInternalLinkWithCurrency = props =>
    getInternalLink({
      ...props,
      currency,
    });

  const handlePlayButton = async event => {
    event.preventDefault();

    const video = videoRef?.current;

    if (video) {
      // Vimeo
      if (video?.player) {
        const isVideoPaused = await video.player?.getPaused();
        if (isVideoPaused === true) {
          video.player?.play();
          setIsVideoPlaying(true);
        } else {
          video.player?.pause();
          setIsVideoPlaying(false);
        }
        return;
      }

      // Internal Sanity Video
      if (video?.paused === true) {
        video?.play();
        setIsVideoPlaying(true);
      } else {
        video?.pause();
        setIsVideoPlaying(false);
      }
    }
  };

  const handlePlayerError = () => {
    setIsError(true);
    setIsLoading(false);
  };

  const handleIsPlaying = () => {
    setIsLoading(false);
  };

  const handleVideoPlayerLoad = () => {
    if (!matchMedia('(prefers-reduced-motion)').matches) {
      const video = videoRef?.current;

      if (video?.player) {
        video.player?.play();
        setIsVideoPlaying(true);
      }
    }
  };

  useEffect(() => {
    const video = videoRef?.current;

    if (video && !video?.player) {
      if (matchMedia('(prefers-reduced-motion)').matches) {
        video.pause();
        setIsVideoPlaying(false);
      } else {
        video.play();
        setIsVideoPlaying(true);
      }
    }
  }, []);

  return (
    <Presentation className="hero" presentation={presentation}>
      <Helmet>
        {/* Pre-load hero image */}
        {mediaItem?.asset?._id && (
          <link
            rel="preload"
            as="image"
            href={
              buildSrc({
                id: mediaItem.asset._id,
                baseUrl: sanityBaseUrl,
              })?.src
            }
            imageSrcSet={buildSrcSet({
              id: mediaItem.asset._id,
              baseUrl: sanityBaseUrl,
            })}
          />
        )}

        {/* Pre-load hero video poster */}
        {poster && <link rel="preload" as="image" href={poster} />}
      </Helmet>

      {!(mediaItem || heroType === 'internalVideo' || heroType === 'vimeo') && (
        <>
          <HeroTextColor
            headlineStyle={headlineStyle}
            subheadlineStyle={subheadlineStyle}
          >
            {getHeadline(
              headline1,
              headline2,
              portableTextHeadline,
              subheadType,
              enableTextShadow
            )}
          </HeroTextColor>
          {getCta(
            enableCta,
            cta,
            getInternalLinkWithCurrency,
            themeStyle || 'primary'
          )}
        </>
      )}

      {(mediaItem || heroType === 'internalVideo' || heroType === 'vimeo') && (
        <HeroInner textAlign={textAlignment || ''}>
          <Box>
            <HeroText
              textAlign={textAlignment || ''}
              presentation={presentation}
            >
              <GridItem textAlign={textAlignment || ''}>
                <HeroTextColor
                  headlineStyle={headlineStyle}
                  subheadlineStyle={subheadlineStyle}
                >
                  {getHeadline(
                    headline1,
                    headline2,
                    portableTextHeadline,
                    subheadType,
                    enableTextShadow
                  )}
                </HeroTextColor>

                {getCta(
                  enableCta,
                  cta,
                  getInternalLinkWithCurrency,
                  themeStyle || 'primary'
                )}
              </GridItem>
            </HeroText>

            {(!heroType || heroType === '' || heroType === 'image') &&
              mediaItem !== null &&
              getHeroImage(mediaItem, mobileCrop, compactImage)}

            {heroType === 'internalVideo' && internalVideo?.video?.url && (
              <video
                ref={videoRef}
                loop
                muted
                playsinline="false"
                poster={poster}
                preload="none"
              >
                <source src={internalVideo.video.url} />
                {tt(s => s.heroComponent.unsupportedVideoTagFallbackText)}
              </video>
            )}

            {heroType === 'vimeo' && (
              <VideoPreview
                src={poster}
                alt={tt(s => s.heroComponent.videoPosterAlt)}
                isLoading={isLoading}
                isError={isError}
              />
            )}

            {heroType === 'vimeo' && !isError && (
              <StyledVimeo
                ref={videoRef}
                video={externalVideo}
                responsive
                muted
                loop
                hideWatermarks
                controls={false}
                playsInline={false}
                dnt
                onLoaded={handleVideoPlayerLoad}
                onPlaying={handleIsPlaying}
                onError={handlePlayerError}
                isLoading={isLoading}
              />
            )}

            {(heroType === 'internalVideo' || heroType === 'vimeo') &&
              !isError && (
                <ShadcnButon
                  variant="secondary"
                  className="absolute right-1 top-1 size-11 z-[1]"
                  aria-label={
                    isVideoPlaying
                      ? tt(s => s.heroComponent.pauseBgHeroVideoAriaLabel)
                      : tt(s => s.heroComponent.playBgHeroVideoAriaLabel)
                  }
                  onClick={handlePlayButton}
                >
                  {isVideoPlaying ? (
                    <FaPause size={24} />
                  ) : (
                    <FaPlay size={24} />
                  )}
                </ShadcnButon>
              )}
          </Box>
        </HeroInner>
      )}
    </Presentation>
  );
}

Hero.propTypes = {
  hero: PropTypes.shape({
    heroType: PropTypes.string,
    compactImage: PropTypes.bool,
    externalVideo: PropTypes.string,
    internalVideo: PropTypes.shape({
      video: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    heroPoster: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    textAlignment: PropTypes.string.isRequired,
    headline1: PropTypes.string,
    headline2: PropTypes.string,
    headlineStyle: PropTypes.string,
    subheadType: PropTypes.string,
    portableTextHeadline: PropTypes.arrayOf(PropTypes.shape({})),
    subheadlineStyle: PropTypes.string,
    mediaItem: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.shape({}),
    ]),
    mobileCrop: PropTypes.string,
    alt: PropTypes.string,
    enableTextShadow: PropTypes.bool,
    enableCta: PropTypes.bool,
    cta: PropTypes.shape({
      linkType: PropTypes.string,
      internalLink: PropTypes.shape({}),
      caption: PropTypes.string,
      href: PropTypes.string,
    }),
    themeStyle: PropTypes.string,
    presentation: PropTypes.string,
    altText: PropTypes.string,
  }),
  getInternalLink: PropTypes.func.isRequired,
};

export default Hero;
